<template>
  <div id="meetings-tab">
    <k-panel :hasContentToggle="false">
      <template #body>
        <k-table class="meetings-table"
          :headers="kableHeaders"
          :rows="kableRows"
          :hideable="false"
          :panel="false"
          :max="10"
          :loading="!meetingsReady"
          @clicked="handleTableAction"
        ></k-table>
        <signatures :showModal="showSignaturesModal"
          :meeting="selectedMeeting"
          @close="closeSignaturesModal"
        ></signatures>
        <assign-meeting-owner :showModal="showMeetingOwnerModal"
                              :meeting="selectedMeeting"
                              @update="getMeetings()"
                              @close="closeMeetingOwnerModal()">
        </assign-meeting-owner>
      </template>
    </k-panel>
  </div>
</template>

<script>
import TimeMixin from '@mixins/time-mixins';
import TextFormattingMixin from '@mixins/text-formatting-mixins';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import Signatures from '../../monitor/meetings/components/signatures.vue';
import AssignMeetingOwner from '../../monitor/meetings/components/assign-meeting-owner.vue';

export default {
  components: {
    AssignMeetingOwner,
    KPanel,
    KTable,
    Signatures,
  },

  mixins: [TimeMixin, TextFormattingMixin],

  beforeMount() {
    this.getMeetings();
  },

  watch: {
    programmeId() {
      this.getMeetings();
    },
  },

  props: {
    userId: {
      type: Number,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      meetingsReady: false,
      meetings: [],
      selectedMeeting: {},
      showSignaturesModal: false,
      showMeetingOwnerModal: false,
    };
  },

  computed: {
    kableHeaders() {
      const headers = {
        meetingName: {
          name: 'Meeting',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        type: {
          name: 'Type',
          type: 'pill',
          sortable: true,
          options: {
            type: status => {
              const classes = {
                Adhoc: 'warning',
              };
              return classes[status];
            },
          },
        },
        meetingOwner: {
          name: 'Owner',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        supportRole: {
          name: 'Role',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        deadline: {
          name: 'Deadline',
          type: 'datetime',
          options: {
            datetimeFormat: 'MMM d, yyyy',
          },
          filterable: true,
          sortable: true,
        },
        meetingStart: {
          name: 'Date',
          type: 'timestamp',
          filterable: true,
          sortable: true,
        },
        status: {
          name: 'Status',
          type: 'pill',
          filterable: true,
          sortable: true,
          options: {
            type: status => {
              const classes = {
                'Not Scheduled': 'neutral',
                Upcoming: 'proposed',
                'In Review': 'primary',
                Completed: 'success',
              };
              return classes[status];
            },
          },
        },
        viewForm: {
          name: 'View Form',
          type: 'button',
          options: {
            preset: 'open',
            title: 'View Form',
            ariaLabel: 'View Form',
            disabled: row => !['Completed', 'In Review', 'Upcoming'].includes(row.status),
          },
        },
        viewSignatures: {
          name: 'View Signatures',
          type: 'button',
          options: {
            preset: 'view',
            title: 'View Signatures',
            ariaLabel: 'View Signatures',
          },
        },
      };

      if (this.$permissions.hasPermission('change_meeting_owners')) {
        headers.changeMeetingOwner = {
          name: 'Change Meeting Owner',
          type: 'button',
          options: {
            preset: 'user',
            title: 'Change Meeting Owner',
            ariaLabel: 'Change Meeting Owner',
            callback: this.handleTableAction,
            disabled: row => ['In Review', 'Completed'].indexOf(row.status) >= 0,
          },
        };
      }

      if (this.$permissions.hasPermission('manage_user_module_assets')) {
        headers.removeMeeting = {
          name: 'Remove',
          type: 'button',
          options: {
            preset: 'delete',
            disabled: row => !row.is_standard_programme_asset,
            title: 'Remove standard programme meeting',
            ariaLabel: 'Remove standard programme meeting',
            callback: this.handleTableAction,
          },
        };
      }

      return headers;
    },

    kableRows() {
      return this.meetings.map(meeting => ({
        ...meeting,
        meetingName: meeting.meeting_name,
        type: !meeting.is_standard_programme_asset ? 'Adhoc' : '-',
        meetingOwner: this.getMeetingOwner(meeting),
        deadline: meeting.deadline,
        meetingStart: meeting.meeting_start,
        supportRole: this.startCase(this.getLearnerSupportRole(meeting)),
        status: meeting.status,
        viewForm: '-',
        viewSignatures: '-',
      }));
    },
  },

  methods: {
    getMeetingOwner(meeting) {
      if (meeting.meeting_owner_name) {
        return meeting.meeting_owner_name;
      }
      if (meeting.support_function === 'coach') {
        return meeting.coach_name;
      }
      if (meeting.support_function === 'data_mentor') {
        return meeting.data_mentor_name;
      }
      return null;
    },
    getLearnerSupportRole(meeting) {
      return meeting.meeting_owner_role || meeting.support_function;
    },
    allSignaturesSigned(meeting) {
      const roles = meeting?.signatures.map(signature => signature.signee_role);
      return roles.includes('learner') && roles.includes('meeting_owner');
    },
    closeSignaturesModal() {
      this.showSignaturesModal = false;
      this.selectedMeeting = {};
    },
    closeMeetingOwnerModal() {
      this.showMeetingOwnerModal = false;
      this.selectedMeeting = {};
    },
    handleTableAction(key, index, row, header) {
      if (header === 'changeMeetingOwner') {
        this.showMeetingOwnerModal = true;
        this.selectedMeeting = this.meetings[index];
      }
      if (header === 'viewForm') {
        const { href } = this.$router.resolve({
          name: 'dash_learner_meeting_form',
          params: {
            studentId: this.userId,
            moduleMeetingId: this.meetings[index].module_meeting_id,
          },
          query: {
            from: 'learner_profile',
          },
        });
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      if (header === 'viewSignatures') {
        this.showSignaturesModal = true;
        this.selectedMeeting = this.meetings[index];
      }
      if (header === 'removeMeeting') {
        this.$ktoast.confirmToast(
          `You are about to remove the standard programme meeting <b>'${row.meetingName}'</b> for <b>'${row.student_name}'</b>.
          <br><br>
          The meeting will no longer be available to the learner, and will not appear in the Meetings Inbox of the learner's ${row.supportRole}.`,
          'warning',
          () => {
            this.removeMeeting(row);
          },
        );
      }
    },
    getMeetings() {
      this.meetingsReady = false;
      this.$logger.info('Fetch all meetings for learner support');
      this.$http.get(`/api/curriculum/meetings/user/${this.userId}/programme/${this.programmeId}`)
        .then(response => {
          this.$logger.info('Successfully fetched all meetings for learner support');
          this.meetings = response.data || [];
        })
        .catch(error => {
          this.$logger.error('Error fetching all meetings for learner support', undefined, error);
          this.$ktoast.showError(error, true);
        }).then(() => {
          this.meetingsReady = true;
        });
    },
    removeMeeting(row) {
      const endpoint = `/api/curriculum/admin/meeting/module/${row.module_meeting_id}/learner/${row.student_id}`;
      this.meetingsReady = false;
      const loggerPayload = {
        moduleMeetingId: row.module_meeting_id,
        learnerUserId: row.student_id,
      };
      this.$logger.info('Removing standard programme meeting for learner', loggerPayload);
      this.$http.delete(endpoint)
        .then(() => {
          this.$logger.info('Successfully removed standard programme meeting for learner', loggerPayload);
          this.$ktoast.success('Meeting successfully removed');
        })
        .catch(error => {
          this.$logger.error('Error removing standard programme meeting for learner', loggerPayload, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.meetingsReady = false;
          this.getMeetings();
        });
    },
  },
};
</script>
